import { useState } from 'react';
import { Button, Form, Table, Accordion } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { RootState } from 'redux/store';
import { downloadNodeInstanceDirectory, getNodeInstanceDirectoryDownloadError } from 'helpers/nodeInstanceDownloads';
import { formatTimestamp, getLocalTimezoneLabel } from 'helpers/formatDateToLocalTimezone';
import { selectHour12Option } from 'redux/selectors/userSettings';
import { useNodeNavigation } from '../../NodeNavigationContext';
import OperatorMessagePanel from './OperatorMessagePanel';

const msToHhmmss = (ms: number | null | undefined): string | null => {
  if (ms === null || ms === undefined) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map((v) => String(v).padStart(2, '0')).join(':');
};

const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);
  return `${size.toFixed(size < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const ReadOnlyProperties = ({ state, onClose = null }) => {
  const [showDescriptions, setShowDescriptions] = useState(false);
  const [isDownloadingDirectory, setIsDownloadingDirectory] = useState(false);
  const [directoryDownloadError, setDirectoryDownloadError] = useState('');
  const { t } = useTranslation();
  const nav = useNodeNavigation();
  const userData = useSelector((store: RootState) => store.auth.userData);
  const localTimezone = userData?.user_settings?.useLocalTimezone === true;
  const localTimezoneLabel = getLocalTimezoneLabel();
  const hour12 = useSelector(selectHour12Option);

  const {
    name = '',
    type,
    sub_type,
    status,
    sub_status,
    scheduled_start_datetime,
    scheduled_start_timezone,
    scheduled_by,
    worker_name,
    output,
    // files,
    exit_code,
    error,
    worker_pid,
    remote_id,
    activated_handles,
    parameters = [],
    default_queue_name,
    queue_name,
    lock_name,
    id,
    node_instance_id,
    parent_node_instance_id,
    flow_parent_instance_id,
    hasChildren,
    event_name,
    process_event_name,
    file_event_instance_id,
    file_event_zone_name,
    can_get_directory,
    spawned_node_instance_id,
    originating_node_instance_id,
    originating_node_definition_name,
    originating_flow_instance_id,
    originating_flow_definition_name,
    submit_and_forget,
    recurrence_definition,
    schedule_display,
    previous_node_instance_id,
    original_anchor_datetime,
  } = state;

  const files = state.files || state.outputdetails?.files || [];
  const columns = state.outputdetails?.columns || null;

  const formatOutputWithColumnOrder = (output, columns) => {
    if (!output || !Array.isArray(output) || !columns?.length) {
      if (output === null || output === undefined) return '';
      return typeof output === 'object' && output !== null ? JSON.stringify(output, null, 2) : output;
    }

    const orderedOutput = output.map((row) => {
      if (typeof row !== 'object' || row === null) return row;
      const orderedRow = {};
      columns.forEach((col) => col in row && (orderedRow[col] = row[col]));
      Object.keys(row).forEach((key) => !(key in orderedRow) && (orderedRow[key] = row[key]));
      return orderedRow;
    });

    return JSON.stringify(orderedOutput, null, 2);
  };

  // Status -> additional a modal when you click on status which shows status_updates (array of objects with datetime, new_status and message)
  // Output (show one line, when click on it a modal with all details. Line breaks are \n, should render fine in a textarea)
  // Files (show list of property filename, on click a modal with the content, only clickable if filecontent != null)

  const scheduledTimezone = scheduled_start_timezone || 'UTC';
  const localeDate = formatTimestamp(scheduled_start_datetime, {
    localTimezone,
    hour12,
    sourceTimezone: scheduledTimezone,
    fallbackTimezone: 'UTC',
    invalidValue: typeof scheduled_start_datetime === 'string' ? scheduled_start_datetime : '',
  });

  const originalAnchorDate = formatTimestamp(original_anchor_datetime, {
    localTimezone,
    hour12,
    sourceTimezone: scheduledTimezone,
    fallbackTimezone: 'UTC',
    invalidValue: typeof original_anchor_datetime === 'string' ? original_anchor_datetime : '',
  });

  const parentInstanceId = flow_parent_instance_id ?? parent_node_instance_id ?? null;

  const openChildNodes = () => {
    nav.open(id, 'child');
  };

  const openPreviousInstance = () => {
    if (previous_node_instance_id == null) return;
    nav.open(previous_node_instance_id, 'previous');
  };

  const openParentNode = () => {
    if (parentInstanceId == null) return;
    nav.open(parentInstanceId, 'parent');
  };

  const isMeaningful = (v) => {
    if (v === 0) return true;
    if (v === false) return true;
    if (v === null || v === undefined) return false;
    if (typeof v === 'string') return v.trim() !== '';
    return true;
  };

  const pickFirstWithSource = (pairs) => {
    for (const [source, val] of pairs) {
      if (isMeaningful(val)) return { value: val, source };
    }
    return { value: null, source: null };
  };

  const formatPreview = (v) => {
    if (typeof v === 'object') {
      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    }
    return String(v);
  };

  const displayType = type === 'shell' && sub_type ? `Shell (${sub_type})` : type === 'shell' ? 'Shell' : type;

  const openSpawnedInstance = () => {
    if (spawned_node_instance_id == null) return;
    nav.open(spawned_node_instance_id, 'spawned');
  };

  const openOriginatingInstance = () => {
    if (originating_node_instance_id == null) return;
    nav.open(originating_node_instance_id, 'originating');
  };

  const openOriginatingFlowInstance = () => {
    if (originating_flow_instance_id == null) return;
    nav.open(originating_flow_instance_id, 'originating');
  };

  const originatingReferenceIsInFlow = originating_flow_instance_id != null;
  const originatingReferenceLabel = originatingReferenceIsInFlow ? 'Originating flow step' : 'Originating submit and forget step';

  /**
   * Event History, pre-filtered to this file event's name and zone.
   *
   * A SEARCH, not an exact scope: `event_name` is matched as a substring, so an
   * event named `Load` also brings back `LoadDaily` and `ReloadStock`. Only an
   * event_id would be exact, and this panel does not have one — it carries the
   * zone and the name. Labelled as a search rather than made exact with a
   * lookup, because the destination is provisional: single objects are getting
   * detail pages, and this link belongs on one of those rather than on a list
   * filtered down to one row.
   *
   * History rather than surplus because the panel describes an instance that
   * already exists, so the arrival that fed it has been consumed.
   */
  const getFileEventUrl = () => {
    if (!file_event_zone_name || !event_name) return null;

    // Detect current origin (works in browser). Fallback to the old host if origin isn't available.
    const origin = window.location?.origin;
    const params = new URLSearchParams({ event_name: String(event_name), zone_name: String(file_event_zone_name) });

    return `${origin}/automation/event_history?${params.toString()}`;
  };

  const getProcessEventUrl = (eventName: string) => {
    if (!eventName) return null;
    const origin = window.location?.origin;
    const params = new URLSearchParams({
      name: eventName,
      type: 'processEvent',
      page: '1',
    });
    return `${origin}/automation/events?${params.toString()}`;
  };

  const processEventNames =
    typeof process_event_name === 'string'
      ? process_event_name
          .split(',')
          .map((name) => name.trim())
          .filter(Boolean)
      : [];

  const handleDownloadDirectory = async () => {
    if (!node_instance_id || isDownloadingDirectory) return;

    setDirectoryDownloadError('');
    setIsDownloadingDirectory(true);

    try {
      await downloadNodeInstanceDirectory(node_instance_id);
    } catch (error: any) {
      const errorMessage = await getNodeInstanceDirectoryDownloadError(error);
      setDirectoryDownloadError(errorMessage);
    } finally {
      setIsDownloadingDirectory(false);
    }
  };

  // Show inline operator message response panel when viewing a workflowActionCreate
  const isWorkflowActionCreate = type === 'workflowActionCreate';
  const isPendingResponse = status === 'WorkflowWait';
  const workflowIdParam = isWorkflowActionCreate ? (parameters.find((p) => p.name === 'workflow_id')?.value ?? null) : null;

  return (
    <>
      <Form.Group className="mb-2">
        <Form.Label>
          Viewing sub node: {node_instance_id} - {name}{' '}
        </Form.Label>
      </Form.Group>
      {hasChildren && (
        <Button className="mb-3" onClick={openChildNodes}>
          Show child nodes (opens in new tab) {hasChildren}{' '}
        </Button>
      )}

      {submit_and_forget === true && (
        <Form.Group className="mb-2">
          <Form.Label>Flow placement</Form.Label>
          <Form.Control disabled type="text" value="Submit and forget" />
        </Form.Group>
      )}

      {spawned_node_instance_id != null && (
        <Form.Group className="mb-2">
          <Form.Label className="d-inline-flex align-items-center gap-1" style={{ cursor: 'pointer' }}>
            <span>Spawned instance (detached)</span>
            <Button
              variant="link"
              type="button"
              className="p-0 text-reset text-decoration-none hover-text-1100 d-inline-flex align-items-center"
              onClick={openSpawnedInstance}
              title="Open spawned instance"
            >
              <FontAwesomeIcon icon={faLink} className="text-600" size="sm" />
            </Button>
          </Form.Label>
          <Form.Control disabled type="text" value={spawned_node_instance_id} />
        </Form.Group>
      )}

      {originating_node_instance_id != null && (
        <>
          <Form.Group className="mb-2">
            <Form.Label className="d-inline-flex align-items-center gap-1" style={{ cursor: 'pointer' }}>
              <span>{originatingReferenceLabel}</span>
              <Button
                variant="link"
                type="button"
                className="p-0 text-reset text-decoration-none hover-text-1100 d-inline-flex align-items-center"
                onClick={openOriginatingInstance}
                title={`Open ${originatingReferenceLabel.toLowerCase()}`}
              >
                <FontAwesomeIcon icon={faLink} className="text-600" size="sm" />
              </Button>
            </Form.Label>
            <Form.Control
              disabled
              type="text"
              value={
                originating_node_definition_name
                  ? `${originating_node_definition_name} (${originating_node_instance_id})`
                  : String(originating_node_instance_id)
              }
            />
          </Form.Group>
          {originating_flow_instance_id != null && (
            <Form.Group className="mb-2">
              <Form.Label className="d-inline-flex align-items-center gap-1" style={{ cursor: 'pointer' }}>
                <span>Parent flow instance</span>
                <Button
                  variant="link"
                  type="button"
                  className="p-0 text-reset text-decoration-none hover-text-1100 d-inline-flex align-items-center"
                  onClick={openOriginatingFlowInstance}
                  title="Open parent flow instance"
                >
                  <FontAwesomeIcon icon={faLink} className="text-600" size="sm" />
                </Button>
              </Form.Label>
              <Form.Control
                disabled
                type="text"
                value={
                  originating_flow_definition_name
                    ? `${originating_flow_definition_name} (${originating_flow_instance_id})`
                    : String(originating_flow_instance_id)
                }
              />
            </Form.Group>
          )}
        </>
      )}

      {parentInstanceId != null && parentInstanceId !== '' && (
        <Form.Group className="mb-2">
          <Form.Label className="d-inline-flex align-items-center gap-1" style={{ cursor: 'pointer' }}>
            <span>Parent Node Instance ID</span>
            <Button
              variant="link"
              type="button"
              className="p-0 text-reset text-decoration-none hover-text-1100 d-inline-flex align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={openParentNode}
              title={t('open-parent-instance')}
              aria-label={t('open-parent-instance')}
            >
              <FontAwesomeIcon icon={faLink} className="text-600" size="sm" />
            </Button>
          </Form.Label>
          <Form.Control disabled type="text" name="Parent Node Instance ID" value={parentInstanceId} />
        </Form.Group>
      )}

      {previous_node_instance_id != null && (
        <Form.Group className="mb-2">
          <Form.Label className="d-inline-flex align-items-center gap-1" style={{ cursor: 'pointer' }}>
            <span>Previous Instance</span>
            <Button
              variant="link"
              type="button"
              className="p-0 text-reset text-decoration-none hover-text-1100 d-inline-flex align-items-center"
              onClick={openPreviousInstance}
              title="Open previous instance in the recurrence chain"
            >
              <FontAwesomeIcon icon={faLink} className="text-600" size="sm" />
            </Button>
          </Form.Label>
          <Form.Control disabled type="text" value={previous_node_instance_id} />
        </Form.Group>
      )}

      <Form.Group className="mb-2">
        <Form.Label>Type</Form.Label>
        <Form.Control disabled type="text" name="Type" value={displayType} />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>Status</Form.Label>
        <Form.Control disabled type="text" name="Status" value={sub_status ? `${sub_status === 'Console' ? 'Waiting' : status} (${sub_status})` : status} />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>Scheduled Start Datetime</Form.Label>
        <Form.Control
          disabled
          type="text"
          name="Scheduled Start Datetime"
          value={localeDate ? `${localeDate} (${localTimezone ? localTimezoneLabel : scheduledTimezone})` : ''}
        />
      </Form.Group>

      {recurrence_definition && (
        <Form.Group className="mb-2">
          <Form.Label>Recurrence Definition</Form.Label>
          <Form.Control disabled type="text" name="Recurrence Definition" value={recurrence_definition} />
        </Form.Group>
      )}

      {schedule_display && (
        <Form.Group className="mb-2">
          <Form.Label>Schedule</Form.Label>
          <Form.Control disabled type="text" name="Schedule" value={schedule_display} />
        </Form.Group>
      )}

      {original_anchor_datetime && (
        <Form.Group className="mb-2">
          <Form.Label>Original Anchor Datetime</Form.Label>
          <Form.Control
            disabled
            type="text"
            name="Original Anchor Datetime"
            value={originalAnchorDate ? `${originalAnchorDate} (${localTimezone ? localTimezoneLabel : scheduledTimezone})` : ''}
          />
        </Form.Group>
      )}

      <Form.Group className="mb-2">
        <Form.Label>Scheduled By</Form.Label>
        <Form.Control disabled type="text" name="Scheduled By" value={scheduled_by || ''} />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>Worker Name</Form.Label>
        <Form.Control disabled type="text" name="Worker Name" value={worker_name ?? ''} />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>Queue</Form.Label>
        <Form.Control disabled type="text" name="Queue" value={queue_name ?? ''} />
      </Form.Group>

      {state.label && (
        <Form.Group className="mb-2">
          <Form.Label>Label</Form.Label>
          <Form.Control disabled type="text" name="Label" value={state.label} />
        </Form.Group>
      )}

      {lock_name && (
        <Form.Group className="mb-2">
          <Form.Label>Locks</Form.Label>
          <Form.Control disabled type="text" name="Locks" value={lock_name} />
        </Form.Group>
      )}

      {(state.runtime_limit_min || state.runtime_limit_max) && (
        <Form.Group className="mb-2">
          <Form.Label>Runtime Limits</Form.Label>
          <Form.Control
            disabled
            type="text"
            name="Runtime Limits"
            value={`Min: ${msToHhmmss(state.runtime_limit_min) || 'N/A'}, Max: ${msToHhmmss(state.runtime_limit_max) || 'N/A'}`}
          />
        </Form.Group>
      )}

      {event_name && (
        <Form.Group className="mb-2">
          <Form.Label>File Event</Form.Label>
          <div className="d-flex align-items-center gap-2">
            {getFileEventUrl() ? (
              // The name is a substring search, so the title says "search"
              // rather than promising this event's history alone.
              <a href={getFileEventUrl()} target="_blank" rel="noopener noreferrer" title="Search Event History for this name">
                {event_name}
              </a>
            ) : (
              <span>{event_name}</span>
            )}

            {file_event_instance_id && (
              <span className="text-muted" title="File event instance id">
                [{file_event_instance_id}]
              </span>
            )}
          </div>
        </Form.Group>
      )}

      {processEventNames.length > 0 && (
        <Form.Group className="mb-2">
          <Form.Label>Process Events</Form.Label>
          <div className="d-flex align-items-center flex-wrap gap-1">
            {processEventNames.map((eventName, index) => (
              <span key={`${eventName}-${index}`}>
                <a href={getProcessEventUrl(eventName) || undefined} target="_blank" rel="noopener noreferrer">
                  {eventName}
                </a>
                {index < processEventNames.length - 1 ? ',' : ''}
              </span>
            ))}
          </div>
        </Form.Group>
      )}

      <Form.Group className="mb-2">
        <Form.Label>Output</Form.Label>
        <Form.Control disabled type="text" as="textarea" rows={6} name="Output" value={formatOutputWithColumnOrder(output, columns)} />
      </Form.Group>

      {can_get_directory && (
        <Form.Group className="mb-2">
          <Form.Label>Directory</Form.Label>
          <div className="d-flex flex-column align-items-start gap-2">
            <Button variant="secondary" onClick={handleDownloadDirectory} disabled={isDownloadingDirectory}>
              {isDownloadingDirectory ? 'Downloading...' : 'Download all files (.zip)'}
            </Button>
            {directoryDownloadError && <small className="text-danger">{directoryDownloadError}</small>}
          </div>
        </Form.Group>
      )}

      {/* Inline operator message response — shown when this node is a pending workflow action */}
      {isWorkflowActionCreate && isPendingResponse && workflowIdParam && (
        <Form.Group className="mb-2">
          <Form.Label>Operator Message</Form.Label>
          <OperatorMessagePanel workflowId={workflowIdParam} />
        </Form.Group>
      )}

      {!!parameters?.length && (
        <Form.Group className="mb-2">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <Form.Label className="mb-0">Parameters</Form.Label>
            <Form.Check
              type="switch"
              id="show-descriptions-switch"
              label="Show descriptions"
              checked={showDescriptions}
              onChange={(e) => setShowDescriptions(e.target.checked)}
            />
          </div>

          <Accordion alwaysOpen flush>
            {parameters.map((handle, index) => {
              const { name: paramName, parameter_type, default_expression, output_format, submit_value, value, out_value, inout, description } = handle;

              const outVal = out_value ?? null;

              const inVal = value ?? null;

              const submitVal = submit_value ?? null;

              const defaultExpr = default_expression ?? null;

              const pickResult = pickFirstWithSource([
                ['out', outVal],
                ['in', inVal],
                ['submit', submitVal],
                ['default', defaultExpr],
              ]);

              const previewRaw = pickResult.value;
              const preview = previewRaw !== null ? formatPreview(previewRaw) : null;

              const displayLabel = showDescriptions && description ? description : paramName;
              const headerLabel = (() => {
                if (!isMeaningful(preview)) return displayLabel;
                return `${displayLabel} [${preview}]`;
              })();

              return (
                <Accordion.Item className="ms-2" eventKey={`${index}`} key={`param-${index}-${paramName}`}>
                  <Accordion.Header>{headerLabel}</Accordion.Header>
                  <Accordion.Body>
                    <Table size="sm" className="m-0" striped bordered hover>
                      <tbody>
                        <tr>
                          <td>Name: </td>
                          <td>{paramName}</td>
                        </tr>

                        {type === 'workflowActionCreate' && paramName === 'workflow_id' && isMeaningful(pickResult.value) && (
                          <tr>
                            <td>Workflow Link:</td>
                            <td>
                              <a
                                href={`/inbox/${pickResult.value}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: '#0d6efd',
                                  textDecoration: 'underline',
                                }}
                              >
                                View in Inbox (ID: {pickResult.value})
                              </a>
                            </td>
                          </tr>
                        )}

                        {description && (
                          <tr>
                            <td>Description:</td>
                            <td>{description}</td>
                          </tr>
                        )}

                        <tr>
                          <td>Type:</td>
                          <td>{parameter_type}</td>
                        </tr>

                        <tr>
                          <td>Output format:</td>
                          <td>{output_format}</td>
                        </tr>

                        <tr>
                          <td>Default expression:</td>
                          <td>{default_expression}</td>
                        </tr>

                        <tr>
                          <td>Submit value:</td>
                          <td>{submit_value}</td>
                        </tr>

                        <tr>
                          <td>In Value:</td>
                          <td>{value}</td>
                        </tr>

                        <tr>
                          <td>Out value:</td>
                          <td>{out_value}</td>
                        </tr>
                        <tr>
                          <td>Inout:</td>
                          <td>{inout}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </Form.Group>
      )}

      {!!files?.length && (
        <Form.Group className="mb-2">
          <Form.Label>Files</Form.Label>

          <ul className="m-0">
            {files.map((handle) => {
              const { filename, filecontent, filesize } = handle;
              return (
                <li key={filename}>
                  <a
                    href={`/node_instance_file/${node_instance_id}/${encodeURIComponent(filename)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={filecontent}
                  >
                    {filename}
                  </a>
                  {filesize !== undefined && <span className="text-muted ms-2">({formatFileSize(filesize)})</span>}
                </li>
              );
            })}
          </ul>
        </Form.Group>
      )}

      {exit_code !== null && exit_code !== undefined && (
        <Form.Group className="mb-2">
          <Form.Label>Exit Code</Form.Label>
          <Form.Control disabled type="text" name="Exit Code" value={exit_code} />
        </Form.Group>
      )}

      {error && (
        <Form.Group className="mb-2">
          <Form.Label>Error</Form.Label>
          <Form.Control disabled type="text" name="Error" value={error} />
        </Form.Group>
      )}

      {worker_pid && (
        <Form.Group className="mb-2">
          <Form.Label>PID</Form.Label>
          <Form.Control disabled type="text" name="PID" value={worker_pid} />
        </Form.Group>
      )}

      {remote_id && (
        <Form.Group className="mb-2">
          <Form.Label>Remote ID</Form.Label>
          <Form.Control disabled type="text" name="Remote id" value={remote_id} />
        </Form.Group>
      )}

      {!!activated_handles?.length && (
        <Form.Group className="mb-2">
          <Form.Label>Activated Handles</Form.Label>
          <ul className="m-0">
            {activated_handles.map((handle) => {
              const { label, id } = handle;
              return (
                <li key={id} title={id}>
                  {label}
                </li>
              );
            })}
          </ul>
        </Form.Group>
      )}
      {onClose && (
        <Button onClick={onClose} className="mt-4" variant="secondary">
          Close
        </Button>
      )}
    </>
  );
};

export default ReadOnlyProperties;
